import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage, FormattedTime } from 'react-intl';
import styled from 'styled-components';

import {
  DecisivePlayerPickerTaskAppearanceStatus,
  FiatWalletAccountState,
  GameStatus,
  ManagerProgressionTaskState,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BodyS, HeadlineM } from '@sorare/core/src/atoms/typography';
import { Alert } from '@sorare/core/src/atoms/ui/Alert';
import { Dialog } from '@sorare/core/src/components/dialog';
import { CloseButton } from '@sorare/core/src/components/dialog/CloseButton';
import { CreateFiatWalletWithInterstitialModal } from '@sorare/core/src/components/fiatWallet/CreateFiatWalletWithInterstitialModal';
import { InterstitialContextModalMode } from '@sorare/core/src/components/fiatWallet/InterstitialContextModal';
import {
  DialogAdditionalParamKey,
  DialogKey,
} from '@sorare/core/src/components/navigation/WithDialogs';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { glossary } from '@sorare/core/src/lib/glossary';

import { useFiatWalletValidation } from 'components/missions/UnclaimedTask/ClaimButton/useFiatWalletValidation';
import { DecisivePlayerPickerDialog } from 'components/missions/picker/DecisivePlayerPickerDialog';
import { DesicivePickerSlot } from 'components/missions/picker/DesicivePickerSlot';

import { DecisivePlayerPickerTaskClaimStory } from '../DecisivePlayerPickerTaskClaimStory';
import type { DecisivePlayerPickerTaskStatus_decisivePlayerPickerTask } from './__generated__/index.graphql';

const StyledButtonBase = styled(ButtonBase)`
  transition: scale 0.1s ease-in-out;
  &:not(:disabled):hover {
    scale: 1.1;
  }
`;

const StyledDialog = styled(Dialog)`
  padding: var(--double-unit);
`;

const DialogHeader = styled(Horizontal)`
  justify-content: flex-end;
`;

const Slots = styled(Horizontal)`
  margin-bottom: var(--triple-unit);
  --card-avatar-size: calc(var(--unit) * 6);
`;

export const DecisivePlayerPickerTaskStatus = ({
  task,
}: {
  task: DecisivePlayerPickerTaskStatus_decisivePlayerPickerTask;
}) => {
  const { kycUnderReview, needsKYCValidation } = useFiatWalletValidation({
    task,
  });

  const [showCreateFiatWallet, setShowCreateFiatWallet] = useState(false);

  const taskIsCompleted =
    task.aasmState === ManagerProgressionTaskState.COMPLETED;
  const taskIsClaimed = task.aasmState === ManagerProgressionTaskState.CLAIMED;
  const taskIsReady = task.aasmState === ManagerProgressionTaskState.READY;
  const openDialog = useOpenDialog();
  const [showDecisivePlayerPickerDialog, setShowDecisivePlayerPickerDialog] =
    useState<boolean>(false);
  const [showImcompleteTasksDialog, setShowImcompleteTasksDialog] =
    useState<boolean>(false);
  const [defaultSelectedSlotIndex, setDefaultSelectedSlotIndex] =
    useState<number>();
  const [showClaimTaskStory, setShowClaimTaskStory] = useState<boolean>(false);
  const {
    so5: { dailyTasksExpiresAt },
  } = useConfigContext();

  const slotNumber = task.maxAppearancesCount ?? 3;

  const normalizedTaskAppearances = Array.from(
    {
      length: slotNumber,
    },
    (_, index) => {
      return task?.taskAppearances?.find(ta => ta.index === index);
    }
  );

  const editableTaskAppearances = normalizedTaskAppearances.filter(
    t =>
      !t ||
      (t.status === DecisivePlayerPickerTaskAppearanceStatus.READY && !t.locked)
  );

  const claim = async () => {
    if (needsKYCValidation) {
      setShowCreateFiatWallet(true);
      return;
    }
    setShowClaimTaskStory(true);
    setShowImcompleteTasksDialog(false);
  };

  const onEditTaskAppearance = (index: number) => {
    setShowDecisivePlayerPickerDialog(true);
    setDefaultSelectedSlotIndex(index);
  };

  const onClaimTaskAppearance = () => {
    if (editableTaskAppearances.length > 0) {
      setShowImcompleteTasksDialog(true);
      return;
    }
    claim();
  };

  return (
    <>
      {!!showCreateFiatWallet && (
        <CreateFiatWalletWithInterstitialModal
          mode={InterstitialContextModalMode.REWARD}
          onDecline={() => setShowCreateFiatWallet(false)}
          statusTarget={FiatWalletAccountState.VALIDATED_OWNER}
          onDismissActivationSuccess={() => setShowCreateFiatWallet(false)}
          onClose={() => setShowCreateFiatWallet(false)}
          canDismissAfterActivation={false}
        />
      )}
      {!!taskIsClaimed && (
        <Button color="secondary" disabled size="compact">
          <FormattedMessage {...glossary.claimed} />
        </Button>
      )}
      {!!taskIsCompleted && (
        <>
          {kycUnderReview ? (
            <Button
              color="quaternary"
              size="compact"
              onClick={() => setShowCreateFiatWallet(true)}
            >
              <FormattedMessage
                id="PendingDeposit.pending"
                defaultMessage="Pending"
              />
            </Button>
          ) : (
            <Button
              color="primary"
              size="compact"
              onClick={onClaimTaskAppearance}
            >
              <FormattedMessage {...glossary.claimNow} />
            </Button>
          )}
        </>
      )}
      {!!taskIsReady && (
        <Horizontal gap={1.5}>
          {normalizedTaskAppearances.map((taskAppearance, index) => {
            const getAction = () => {
              if (taskAppearance?.locked) {
                if (
                  taskAppearance?.status ===
                    DecisivePlayerPickerTaskAppearanceStatus.SUCCESS &&
                  task.aasmState === ManagerProgressionTaskState.COMPLETED
                ) {
                  return onClaimTaskAppearance;
                }
                if (
                  taskAppearance?.playerGameScore?.anyGame?.statusTyped ===
                    GameStatus.playing ||
                  [
                    DecisivePlayerPickerTaskAppearanceStatus.FAILURE,
                    DecisivePlayerPickerTaskAppearanceStatus.SUCCESS,
                  ].includesValue(taskAppearance?.status)
                ) {
                  return () => {
                    if (taskAppearance?.playerGameScore?.anyGame?.id) {
                      openDialog(
                        DialogKey.game,
                        taskAppearance.playerGameScore.anyGame.id,
                        {
                          additionalParams: {
                            [DialogAdditionalParamKey.playerSlugs]:
                              normalizedTaskAppearances
                                .map(ta => ta?.card?.anyPlayer.slug)
                                .filter(Boolean),
                          },
                        }
                      );
                    }
                  };
                }
                return null;
              }
              return onEditTaskAppearance;
            };

            const action = getAction();
            const hasSucceededTaskAppearanceButCannotClaim =
              taskAppearance?.status ===
                DecisivePlayerPickerTaskAppearanceStatus.SUCCESS &&
              task.aasmState !== ManagerProgressionTaskState.COMPLETED;
            return (
              <StyledButtonBase
                key={taskAppearance?.id || `slot-${index}`}
                onClick={() => {
                  action?.(index);
                }}
                disabled={!action}
              >
                <DesicivePickerSlot
                  taskAppearance={taskAppearance}
                  isSelected={false}
                  index={index}
                  showAction={!hasSucceededTaskAppearanceButCannotClaim}
                  showLockedOverlay={false}
                  mode={task.mode}
                />
              </StyledButtonBase>
            );
          })}
        </Horizontal>
      )}
      {!!showDecisivePlayerPickerDialog && (
        <DecisivePlayerPickerDialog
          taskId={task.id}
          defaultSelectedSlotIndex={defaultSelectedSlotIndex}
          onClose={() => setShowDecisivePlayerPickerDialog(false)}
          onSuccess={() => setShowDecisivePlayerPickerDialog(false)}
        />
      )}
      <StyledDialog
        open={showImcompleteTasksDialog}
        onClose={() => setShowImcompleteTasksDialog(false)}
        maxWidth="xs"
        fullScreen={false}
        hideHeader
      >
        <Vertical gap={2}>
          <DialogHeader>
            <CloseButton onClose={() => setShowImcompleteTasksDialog(false)} />
          </DialogHeader>
          <Slots gap={2} center>
            {normalizedTaskAppearances.map((taskAppearance, index) => (
              <DesicivePickerSlot
                key={taskAppearance?.id || `slot-${index}`}
                taskAppearance={taskAppearance}
                isSelected={false}
                index={index}
                showAction
                showLockedOverlay={false}
                mode={task.mode}
              />
            ))}
          </Slots>
          <Vertical>
            <HeadlineM>
              <FormattedMessage
                id="missions.DailyMissionStatus.challengeIncomplete"
                defaultMessage="Challenge incomplete"
              />
            </HeadlineM>
            <BodyM color="var(--c-nd-600)">
              <FormattedMessage
                id="missions.DailyMissionStatus.challengeIncompleteMessage"
                defaultMessage="You still have <strong>{count, plural, one {# player} other {# players}}</strong> available to edit."
                values={{
                  count: editableTaskAppearances.length,
                  strong: chunks => (
                    <BodyM as="span" color="var(--c-white)" bold>
                      {chunks}
                    </BodyM>
                  ),
                }}
              />
            </BodyM>
          </Vertical>
          <Horizontal>
            <LoadingButton
              color="quaternary"
              size="medium"
              loading={showClaimTaskStory}
              onClick={claim}
            >
              <FormattedMessage {...glossary.claimNow} />
            </LoadingButton>
            <Button
              size="medium"
              onClick={() => {
                setShowDecisivePlayerPickerDialog(true);
                setShowImcompleteTasksDialog(false);
              }}
            >
              <FormattedMessage
                id="missions.DailyMissionStatus.editPlayer"
                defaultMessage="Edit Player"
              />
            </Button>
          </Horizontal>
          <Alert
            title={
              <FormattedMessage
                id="missions.DailyMissionStatus.warning"
                defaultMessage="Warning"
              />
            }
            message={
              <BodyS>
                <FormattedMessage
                  id="missions.DailyMissionStatus.warningMessage2"
                  defaultMessage="If you claim now, you cannot claim any more rewards until tomorrow {time}."
                  values={{
                    time: (
                      <FormattedTime
                        hour="numeric"
                        value={dailyTasksExpiresAt || undefined}
                      />
                    ),
                  }}
                />
              </BodyS>
            }
          />
        </Vertical>
      </StyledDialog>
      {!!showClaimTaskStory && (
        <DecisivePlayerPickerTaskClaimStory
          task={task}
          onClose={() => {
            setShowClaimTaskStory(false);
          }}
        />
      )}
    </>
  );
};

DecisivePlayerPickerTaskStatus.fragments = {
  decisivePlayerPickerTask: gql`
    fragment DecisivePlayerPickerTaskStatus_decisivePlayerPickerTask on DecisivePlayerPickerTask {
      id
      aasmState
      maxAppearancesCount
      mode
      taskAppearances {
        id
        status
        locked
        card {
          slug
          anyPlayer {
            slug
          }
        }
        playerGameScore {
          id
          anyGame {
            id
            statusTyped
          }
        }
        ...DesicivePickerSlot_taskAppearance
      }

      ...DecisivePlayerPickerTaskClaimStory_decisivePlayerPickerTask
      ...ClaimYourRewardsStory_taskInterface
      ...RewardUnlockedStory_taskInterface
      ...useFiatWalletValidation_taskInterface
      ...ClaimYourRewardsStory_taskInterface
    }
    ${DesicivePickerSlot.fragments.taskAppearance}
    ${DecisivePlayerPickerTaskClaimStory.fragments.taskInterface}
    ${useFiatWalletValidation.fragments.taskInterface}
  ` as TypedDocumentNode<DecisivePlayerPickerTaskStatus_decisivePlayerPickerTask>,
};
